import React, {useCallback, useRef} from 'react';
import styled from 'styled-components';
import {useHighlighterContext, ColorInfo} from 'Contexts/HighlighterContext';
import {NodeLocationInfo, useNodeCallbackContext} from 'Contexts/NodeCallBackContext';
import {PaddingHighlighter} from './PaddingHighlighter';
import {MarginHighlighter} from './MarginHighlighter';
import {WidthHeightLabels} from './WidthHeightLabels';
import {GapHighlighter} from './GapHighlighter';
import {BlurFilter} from './BlurFilter';
import {areNodesAncestors, findNodeById, getWorkingNode} from 'rendition-core';
import {SelectedBlurFilter} from './SelectedBlurFilter';
import {usePreviewContext} from 'Contexts/PreviewContext';
import {HoverBlurFilter} from './HoverBlurFilter';
import {useAnimationFrame} from 'Hooks/useAnimationFrame';

const BORDER_SIZE = 2;

const colorInfoToBorder = (color: ColorInfo) => {
    if (color.outline === false) {
        return `${BORDER_SIZE}px solid transparent`;
    }
    return `${BORDER_SIZE}px ${color.dashed ? 'dashed' : 'solid'} ${color.color}`;
};

export const Highlighter: React.FC<React.PropsWithChildren<{}>> = () => {
    const {nodeCallbacks, scaleRef} = useNodeCallbackContext();

    const {hiddenNodes} = usePreviewContext();
    const {highLightedNodeColors, view} = useHighlighterContext();

    const ref = useRef<HTMLDivElement | null>(null);
    const getCurrentPreviewLocation = () => {
        if (ref.current) {
            const rec = ref.current.getBoundingClientRect();
            return {x: rec.left, y: rec.top};
        }
        return {x: 0, y: 0};
    };

    return (
        <Wrapper ref={ref}>
            {Object.keys(highLightedNodeColors).map((nodeId) => {
                const xy = getCurrentPreviewLocation();

                if (!nodeCallbacks[nodeId]) {
                    return <div key={nodeId}></div>;
                }

                const location = nodeCallbacks[nodeId]().actual;

                const node = findNodeById(view.tree, nodeId);

                const hasHiddenAncestor = areNodesAncestors(view.tree, nodeId, hiddenNodes).length > 0;

                if (!node || hiddenNodes.includes(nodeId) || hasHiddenAncestor) {
                    return <div key={nodeId}></div>;
                }

                const workingNode = getWorkingNode(node, view);
                const colorInfo = highLightedNodeColors[nodeId];

                return (
                    <Outline
                        key={nodeId}
                        border={colorInfoToBorder(colorInfo)}
                        locationCallBack={nodeCallbacks[node.internalId]}
                        rootXY={xy}
                        blur={colorInfo.blur}
                        isHovered={colorInfo.isHovered}
                        opacity={colorInfo.opacity || 1}
                    >
                        {colorInfo.width && <WidthHeightLabels workingNode={workingNode} color={colorInfo.color} />}
                        {colorInfo.showMargin && (
                            <MarginHighlighter
                                scale={scaleRef}
                                node={workingNode}
                                locationCallBack={nodeCallbacks[node.internalId]}
                                verticalOnly={colorInfo.verticalOnly}
                                horizontalOnly={colorInfo.horizontalOnly}
                            />
                        )}
                        {colorInfo.showPadding && (
                            <PaddingHighlighter
                                scale={scaleRef}
                                workingNode={workingNode}
                                locationCallBack={nodeCallbacks[node.internalId]}
                                horizontalOnly={colorInfo.horizontalOnly}
                                verticalOnly={colorInfo.verticalOnly}
                            />
                        )}

                        {colorInfo.gap && (
                            <GapHighlighter
                                view={view}
                                scale={scaleRef}
                                workingNode={workingNode}
                                location={location}
                                horizontalOnly={colorInfo.horizontalOnly}
                                verticalOnly={colorInfo.verticalOnly}
                            />
                        )}

                        {colorInfo.blur && (colorInfo.isHovered ? <HoverBlurFilter /> : <BlurFilter />)}
                        {colorInfo.selectedBlur && <SelectedBlurFilter />}
                    </Outline>
                );
            })}
        </Wrapper>
    );
};

export const Outline: React.FC<
    React.PropsWithChildren<{
        border: string;
        locationCallBack: () => NodeLocationInfo;
        blur?: boolean;
        simulated?: boolean;
        rootXY: {x: number; y: number};
        pointerEvents?: boolean;
        zIndex?: number;
        onClick?: React.MouseEventHandler<HTMLDivElement>;
        isHovered?: boolean;

        borderSize?: number;
        opacity?: number;
    }>
> = ({
    children,
    simulated = false,
    zIndex = 3,
    border,
    locationCallBack,
    rootXY,
    blur,
    onClick,
    pointerEvents = false,
    isHovered,
    borderSize,
    opacity = 1,
}) => {
    const borderSizeToAccountFor = borderSize ?? BORDER_SIZE;

    const outlineRef = useRef<null | HTMLDivElement>(null);

    const updateOnFrame = useCallback(() => {
        if (outlineRef.current) {
            // callback
            const location = locationCallBack();
            if (simulated) {
            } else {
                outlineRef.current.style.width = location.actual.width + 'px';
                outlineRef.current.style.height = location.actual.height + 'px';
                outlineRef.current.style.left = location.actual.x - rootXY.x - borderSizeToAccountFor + 'px';
                outlineRef.current.style.top = location.actual.y - rootXY.y - borderSizeToAccountFor + 'px';
            }
        }
    }, [borderSizeToAccountFor, locationCallBack, rootXY.x, rootXY.y, simulated]);

    useAnimationFrame(updateOnFrame);

    return (
        <NodeOutline
            ref={outlineRef}
            onClick={onClick}
            style={{
                opacity,
                border: border,
                // width: simulated ? location.simulated.width * scale : location.actual.width,
                // height: simulated ? location.simulated.height * scale : location.actual.height,
                // top: simulated
                //     ? location.simulated.y * scale - borderSizeToAccountFor
                //     : location.actual.y - rootXY.y - borderSizeToAccountFor,
                // left: simulated
                //     ? location.simulated.x * scale - borderSizeToAccountFor
                //     : location.actual.x - rootXY.x - borderSizeToAccountFor,
                pointerEvents: pointerEvents ? 'auto' : 'none',
                zIndex: isHovered ? zIndex + 1 : zIndex,
                ...(blur && {
                    borderRadius: '7px',
                }),
            }}
        >
            {children}
        </NodeOutline>
    );
};

const Wrapper = styled.div`
    position: relative;
`;

const NodeOutline = styled.div`
    position: absolute;
    box-sizing: content-box;
`;
